/**
 * `PresentationDirector` — the central animation sequencer for the COMBAT.app map.
 * It sits between the controller-owned **authoritative**
 * `EncounterState` and a full **shadow presentation `EncounterState`** the map
 * renders, advancing presentation toward authoritative one **beat** at a time.
 *
 * > **Core invariant:** when the beat queue is empty, `presentation === target`
 * > (the latest authoritative state). The map renders a deliberately *lagged* view
 * > of truth — the lag is the animation (a token walks rather than teleports).
 *
 * This is the renderer-agnostic spine. It owns the presentation state, the strictly
 * serial beat queue + pump, the diff→beats synthesis (the *diff path*), and the one
 * catch-up primitive `snap`. It has **no Pixi / React dependency**: the one
 * renderer-coupled seam is the injected {@link PresentationAnimator} (a beat awaits
 * its `Promise`) and the {@link MovePlanner} (pure path→world-px geometry). Swapping
 * the renderer rewrites those injected bodies, never this queue.
 *
 * **Open-ended beat vocabulary.** A beat is a discriminated `{ kind }` record; adding
 * one is a one-file change here — extend {@link Beat} and add a case to {@link play}.
 * The *event path* (compiling a resolution's `ResolvedStep[]` into beats) plugs into
 * this same queue via a second ingestion method beside {@link ingest}.
 */
import { applyResolved, type AttackFx, type Cell, type Combatant, type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import { linkCauseName } from "@lancer-vtt/rules";
import { DEFAULT_ATTACK_FX, DETONATION_MS, travelMs } from "./attackFxTiming";
import { chainSegments, type ChainReaction, type ChainSegment, type HoldPolicy } from "./chainSegments";

/**
 * The visual tone of a floating-combat-text beat — drives the renderer's colour, never
 * any logic. `damage`/`heal` are an HP swing, `miss` a whiffed attack, `status` a
 * condition landing/clearing or a change in standing (structure, stress, heat), `verdict` a
 * rolled outcome read off a table or an interception (`STRUCTURE: GLANCING BLOW`,
 * `CUSTOM PAINT JOB: SAVED`), `destroy` a kill. Renderer-agnostic (the Pixi animator
 * maps it to a text fill colour); a different renderer maps it however it likes.
 */
export type FloatTone = "damage" | "heal" | "miss" | "status" | "verdict" | "destroy" | "caption";

/**
 * What a {@link PresentationAnimator.float} renders above a token: the text and its tone.
 * `followOn` marks a float that is not the first one over its token in the same resolution
 * (Decision 2 B of `resolution-step-beats`): the renderer paces it at its compressed follow-on
 * pace instead of the full float pace, so a cascade reads in order without dragging.
 */
export type FloatSpec = { text: string; tone: FloatTone; followOn?: true };

/**
 * A named audio cue a combat beat asks its renderer to play — `impact`
 * for a weapon strike that connects, `miss` for one that whiffs. Renderer-agnostic like {@link FloatTone}:
 * the cue names *what* happened, never *how* it sounds (the Pixi animator maps each to an
 * `SfxEngine` trigger; a non-audio renderer ignores them).
 */
export type SfxCue = "impact" | "miss";

/**
 * Where a {@link PresentationAnimator.projectile}/{@link PresentationAnimator.impact} flourish
 * lands — a live token by id (every single-target beat, and an AoE beat's `from`), or a bare
 * board {@link Cell} (an AoE's shared template-anchor cell, which may
 * have no combatant standing on it — an empty aimed square, or a Line/Cone's far endpoint).
 */
export type AnimAnchor = { kind: "combatant"; id: string } | { kind: "cell"; cell: Cell };

/** How one target's attack came out, as the map draws it. */
export type AttackOutcome = "hit" | "crit" | "miss";

/**
 * What an attack's travel draws (`attack-animations` 2.1): its fx descriptor, read off the recorded
 * step, and every target it is flying at (a missile family flies one per target; a Smart weapon's
 * reticle closes on each). Travel is identical for a hit, a miss and a crit: nothing in it may
 * depend on the outcome, because it plays before the dice land.
 */
export type TravelSpec = { fx: AttackFx; targets: readonly string[] };

/**
 * What lands at one target once the dice are down (`hit`, `crit`, `miss`), or, for `land`, where an
 * area attack's shot comes down before the dice: a Blast's detonation, which always happens. `from`
 * is the attacker, so a family can draw its outcome relative to where the shot came from (a strike
 * snaps its lunging attacker back; a ricochet glances off the side facing away from it). `area`
 * marks a target caught in a template, whose outcome is a short burst rather than the family's
 * own impact.
 */
export type ImpactSpec = { fx: AttackFx; from: string; outcome: AttackOutcome | "land"; area?: true };

/**
 * The renderer's animation surface — the **single renderer-coupled seam** (implemented today by
 * `pixiAnimator.ts`), injected so the Director core stays renderer-agnostic. Every method returns a
 * `Promise` the owning beat awaits; the signatures name *what* to animate (a token id, world-px
 * points), never *how*, so a different renderer's implementation satisfies the same contract.
 */
export type PresentationAnimator = {
  /**
   * Slide token `id` through `points` (footprint-top-left world px, origin first) —
   * the tile-by-tile walk. Resolves when it settles on the last point; resolves
   * immediately if the node is absent (nothing to move). The node is expected to be
   * at `points[0]` already (presentation has not yet committed the destination), so
   * the slide runs *forward* from the current rest position with no flash.
   */
  move(id: string, points: { x: number; y: number }[]): Promise<void>;
  /** A quick blink in place — the teleport / GM-place variant (no legal path to walk). */
  blink(id: string): Promise<void>;
  /** Fade a freshly-committed token in (spawn). Best-effort: resolves immediately if no node yet. */
  fadeIn(id: string): Promise<void>;
  /** Fade a token out before its removal commits (destroy). Best-effort if no node. */
  fadeOut(id: string): Promise<void>;
  /**
   * Float combat text (`-N` / `+N` / `MISS` / a status name) above token `id` — the
   * event-path flourish. Resolves when the float's beat is
   * done pacing (so the serial pump shows AoE outcomes one at a time); resolves
   * immediately if the node is absent (the state still commits — only the flourish skips).
   */
  float(id: string, spec: FloatSpec): Promise<void>;
  /**
   * Play an attack's travel from `fromId`'s token to `to` (a token or a bare cell —
   * {@link AnimAnchor}) in the family `travel.fx` names — the `ProjectileBeat` flourish, played
   * before its outcome (or, for an AoE fan-out, once before ALL of its targets' outcomes). It must
   * look the same whatever the outcome. Resolves once the travel has paced (a trail or a family that
   * holds over its target through the dice may outlive that); resolves immediately if `fromId`'s
   * node (or a `combatant`-anchored `to`'s node) is absent. No `travel` plays the default tracer.
   */
  projectile(fromId: string, to: AnimAnchor, travel?: TravelSpec): Promise<void>;
  /**
   * Play an attack's outcome at `at` (a token or a bare cell — {@link AnimAnchor}) — the
   * `ImpactBeat` flourish: the family's hit, crit or miss look once the dice are down, or a Blast's
   * detonation (`land`) before them. Resolves once it has paced its beat (the visual may keep
   * fading past that); resolves immediately if a `combatant`-anchored `at`'s node is absent (nothing
   * to burst over — the outcome beat still plays and commits state). No `spec` plays a plain hit.
   */
  impact(at: AnimAnchor, spec?: ImpactSpec): Promise<void>;
  /**
   * Detonate a one-shot **tech**-toned burst at `at` (a token or a bare cell — {@link AnimAnchor}) —
   * the flourish for a quick-tech resolution: a hacker-green recolor of the SAME pooled burst
   * {@link impact} plays, never a second particle system. Two callers: (a) a tech attack's HIT
   * (Invade) plays this INSTEAD OF a `projectile`+{@link impact} pair — tech doesn't fire a bullet;
   * (b) a no-roll quick-tech pick (Scan/Lock On/Bolster) plays this ALONGSIDE its existing status
   * float (or alone, for Scan, which floats nothing today). Resolves once the burst has paced
   * its beat; resolves immediately if a `combatant`-anchored `at`'s node is absent, same as
   * {@link impact}.
   */
  techBurst(at: AnimAnchor): Promise<void>;
  /**
   * Punch token `id` on a landed hit (`punch` scales the shake: 2 for a crit) — the flourish played by the
   * (hit-only) `attack` outcome beat CONCURRENTLY with its {@link float}: a quick damped **shake** of
   * the token's node plus a **dark-red pulse** of its silhouette. Purely cosmetic — it perturbs the
   * registered node directly and **must settle it back to the EXACT rest position (and restore its
   * tint) before this `Promise` resolves**, so the renderer never fights the beat and the queue-empty
   * invariant holds. Never played for a `miss`/`status`/`destroyed`. Resolves immediately if the node
   * is absent (nothing to shake — the outcome still floats and commits). Stopped + restored by
   * {@link cancelAll} on a {@link snap}.
   */
  shakeAndPulse(id: string, punch?: number): Promise<void>;
  /**
   * Play a one-shot audio {@link SfxCue} for a combat beat — the `impact` beat asks for `"impact"`,
   * the `miss` beat for `"miss"`. Unlike every other method here this returns
   * **`void`, not a `Promise` the beat awaits**: sound is a pure fire-and-forget side-effect that must
   * NEVER block, delay, or fail a beat's resolution/timing (the Director's "don't block the beat
   * queue" invariant), so the contract deliberately gives the pump nothing to
   * await. The Pixi animator delegates to the app's `SfxEngine` (which lazily inits and swallows its
   * own errors); a non-audio renderer no-ops. Not tied to {@link cancelAll} — a cue is already over.
   */
  sound(cue: SfxCue): void;
  /**
   * Ring these tokens as the chain link now playing: its actor and its target
   * (`chained-resolution-sequence` 2.2). `[]` clears the ring. Like {@link sound} it returns `void`:
   * the ring is standing presentation, not a tween a beat waits on, and the renderer pans to the
   * first id only when that token is off screen.
   */
  focus(ids: readonly string[]): void;
  /** Stop any in-flight tween and resolve its pending `Promise` — called on {@link snap}. */
  cancelAll(): void;
};

/**
 * Resolve the tile-by-tile route a move travels into footprint-top-left **world-px**
 * points (origin first), or `null` for a teleport / GM-place / out-of-range jump (no
 * legal path — the beat blinks instead of skipping). Injected so the Director core
 * carries no board geometry; the frontend supplies it from `movePathAnchors` + the
 * footprint box (the exact route the drag highlight previews).
 */
export type MovePlanner = (id: string, from: Cell, to: Cell) => { x: number; y: number }[] | null;

/**
 * One queued unit of presentation advance.
 *
 * **Diff-path beats**: a **move** stores only the id — it resolves its from
 * (current presentation pos) and to (latest authoritative pos) at *play* time, so
 * re-moving a token mid-walk needs no queue surgery (the next walk starts wherever
 * presentation currently rests). Spawn/destroy carry the id whose existence changed.
 *
 * **Event-path beats**: compiled from a resolution's `ResolvedStep[]` by
 * {@link compileResolution} ("record fine, render coarse" — a grouped step-run becomes
 * one beat). They all carry the floated-above target `id` and (except `commit`) a
 * {@link FloatSpec}. Their state convergence is identical to the diff path — each
 * `commitFromTarget(id)` adopts the *whole* authoritative combatant — so the
 * queue-empty invariant stays a literal identity; the steps drive only what text to
 * float, never the commit. `commit` is the silent catch-all for a step-run that has no
 * float (a resource or ledger op, the attacker's own heat) so every step lands and the
 * invariant holds.
 *
 * **`projectile` and `impact`**: also event-path-compiled, but neither is a diff-path beat nor an
 * {@link EventBeatKind} — each carries no {@link FloatSpec} and commits nothing. For a
 * **single-target** attack, a `to_hit` compiles to a `projectile` beat (the attack's travel, its
 * {@link TravelSpec}), then an `impact` beat (the family's hit, crit or miss look where it lands,
 * its {@link ImpactSpec}), then its `attack`/`miss` outcome beat. Each non-committing beat's `id`
 * mirrors the target so it participates in the same per-id pending/lag bookkeeping every beat uses
 * — the target stays lagged through the travel AND the impact until the outcome beat commits it
 * from target. (That same-id run is exactly why {@link pump}'s pending cleanup is guarded to fire
 * only on a target's LAST queued beat — see the comment there.)
 *
 * For a **multi-target AoE** fan-out (Blast/Burst/Line/Cone), `compileResolution` compiles ONE shared
 * travel (attacker → the resolution's `areaOrigin` cell) and, for a Blast, ONE detonation there
 * (`impact` with outcome `land`), BEFORE every target's own outcome — a large target count would
 * otherwise serialize N travels and blow the backpressure cap ({@link LAG_CAP_MS}). The shared beats'
 * `id` is the *attacker's* id (never a target's — so they can't collide with any target's own
 * pending/lag bookkeeping); their `to`/`at` anchor is a `{ kind: "cell" }` {@link AnimAnchor}, not a
 * combatant id (the aimed cell may be empty).
 */
type Beat =
  | { kind: "move"; id: string }
  // A resolution-recorded displacement (Knockback's `forced_move` step, or a resolution's voluntary
  // `move` step) — animated as a real slide, NOT a silent commit. A `forced_move` falling into the
  // catch-all `commit` beat repositions no sprite, so a Ram's knockback would move the server state
  // while the token never visually moved. Carries the step's OWN baked `from`/`to` cells (playback
  // never re-queries the map), so the
  // slide is correct even after an earlier same-target beat (the attack outcome) has run.
  // `teleport`: the step crossed nothing (the `teleport` atom), so it blinks rather than slides.
  | { kind: "forced_move"; id: string; from: Cell; to: Cell; teleport?: true }
  | { kind: "spawn"; id: string }
  | { kind: "destroy"; id: string }
  | { kind: "projectile"; id: string; from: string; to: AnimAnchor; travel: TravelSpec }
  | { kind: "impact"; id: string; at: AnimAnchor; spec: ImpactSpec }
  // The quick-tech flourish — see
  // `PresentationAnimator.techBurst`'s doc for the two call sites (a tech attack's hit, replacing
  // `projectile`+`impact`; a no-roll quick-tech pick, alongside/instead of its status float).
  | { kind: "tech_burst"; id: string; at: AnimAnchor }
  // Hold the queue until this resolution's 3D dice have stopped
  // rolling. It animates nothing — it is a pure wait, spliced in front of the first beat that would
  // disclose the outcome (see {@link withDiceGate}), so the board stops telling the player what
  // happened while the dice that decided it are still in the air. `wait` is a thunk rather than a
  // live promise so the Director core stays agnostic about where dice come from: the React adapter
  // supplies it, and it isn't called until the beat actually plays.
  | { kind: "dice"; id: string; wait: () => Promise<void> }
  // A chain link's caption (`chained-resolution-sequence` 2.1): `AGGRESSION — BERSERKER` floated
  // over the acting unit before its link plays. A flourish: it commits nothing.
  | { kind: "caption"; id: string; text: string }
  // A reaction's float (4.3): floats like an event beat, commits nothing (see `withReactions`).
  | { kind: "flash"; id: string; float: FloatSpec }
  // Move the focus ring to a link's actor and target, or clear it (`ids: []`). Commits nothing;
  // `id` is {@link FOCUS_BEAT_ID}, which no combatant carries.
  | { kind: "focus"; id: string; ids: string[] }
  // A held roll (`chained-resolution-sequence` 3.1): publish the stakes banner, wait for the hold's
  // release (Roll it), then clear the banner. A pure wait like `dice`; the held roll's own dice
  // gate follows it, so its die tumbles only after the release.
  | { kind: "hold"; id: string; hold: HoldView; wait: () => Promise<void> }
  // Set the state a chain segment's beats commit from (3.3): the record applied through the end of
  // that segment, or `null` for the real authoritative state. What keeps a held structure check
  // from showing its unit already destroyed.
  | { kind: "stage"; id: string; state: EncounterState | null }
  // `punch` scales the target's shake: a crit doubles it, a heavy round hits harder.
  | { kind: "attack"; id: string; float: FloatSpec; punch?: number }
  | { kind: "miss"; id: string; float: FloatSpec }
  | { kind: "status"; id: string; float: FloatSpec }
  | { kind: "destroyed"; id: string; float: FloatSpec }
  | { kind: "commit"; id: string; float: null };

/** The event-beat kinds — those compiled from a resolution (vs the diff-path move/spawn/destroy). */
type EventBeatKind = "attack" | "miss" | "status" | "destroyed" | "commit";

/** The combatant a step is *about* (its target/actor) — the id its beat floats above / commits.
 *  A record-only `damage` has no subject (it always folds into an attack run, never reaching here);
 *  return `""` so the catch-all stays total (`commitFromTarget("")` is a harmless no-op).
 *  `damage` is checked explicitly, ahead of the generic `"target" in step` check, because its
 *  `target` is now OPTIONAL (`resolution-narration` 1.2 provenance) — narrowing on `"target" in
 *  step` alone would let a `damage` step through with a possibly-`undefined` `.target`. */
function subjectOf(step: ResolvedStep): string {
  if (step.op === "save") return step.actor;
  if (step.op === "damage" || step.op === "link") return "";
  if ("target" in step) return step.target;
  return "";
}

/**
 * The steps that BREAK an attack run: a new `to_hit` (the next target in an AoE), a
 * `save`/`status`/`destroy`/`structure_delta` (each its own beat), and a `move`/`forced_move` (its
 * own animated slide beat). Everything else (`damage`, `hp_delta`, heat/stress,
 * `table_check`, `burn_set`, resources) is the *outcome* of the current attack and folds into its run.
 *
 * `structure_delta` breaks the run so a Structure loss gets its OWN "-1 Structure" float — and,
 * load-bearingly, so the reset-to-full `hp_delta` the cascade emits AFTER the loss falls OUT of
 * the attack's HP sum (it lands as a silent `commit`, never a bogus "+N heal" float). The `damage`
 * step always precedes the first `structure_delta` in cascade order, so the attack float still sees
 * the full damage dealt. `forced_move`/`move` break the run so a Knockback push animates as its own
 * slide sequenced AFTER the hit's outcome float, instead of folding into the run and being silently
 * committed (the token never moving); the `damage`/`hp_delta` steps still precede the push, so the
 * attack float keeps the full damage.
 */
function breaksAttackRun(op: ResolvedStep["op"]): boolean {
  return op === "to_hit" || op === "save" || op === "status_add" || op === "status_remove"
    || op === "destroy" || op === "structure_delta" || op === "move" || op === "forced_move";
}

/**
 * The run's HP-dealing `damage` steps. Heat-typed damage is left out: it raises heat rather than
 * taking HP, and its `heat_delta` floats `+N HEAT` as a standing beat of its own.
 */
function hpDamageSteps(run: readonly ResolvedStep[]): Extract<ResolvedStep, { op: "damage" }>[] {
  return run.flatMap((s) => (s.op === "damage" && s.dtype !== "heat" ? [s] : []));
}

/** Short damage-type labels for a per-source damage float (`-5 KIN`, then `-2 BURN`). */
const DTYPE_ABBREV: Record<Extract<ResolvedStep, { op: "damage" }>["dtype"], string> = {
  kinetic: "KIN",
  energy: "EN",
  explosive: "EXP",
  burn: "BURN",
  heat: "HEAT",
  variable: "VAR",
};

/**
 * Compile an attack run (`to_hit` + its folded damage steps) into the floated outcome. The to-hit
 * verdict shares the float (`HIT -7`, `CRIT -14`) so an ordinary hit costs no extra beat. With
 * `damage` given, the float carries that one step's number and type instead of the run's sum (the
 * first float of a per-source split).
 */
function attackFloat(run: readonly ResolvedStep[], damage?: Extract<ResolvedStep, { op: "damage" }>): FloatSpec {
  const verdict = run[0]?.op === "to_hit" && run[0].result === "crit" ? "CRIT" : "HIT";
  if (damage !== undefined) return { text: `${verdict} -${damage.final} ${DTYPE_ABBREV[damage.dtype]}`, tone: "damage" };
  let hp = 0;
  for (const s of run) if (s.op === "hp_delta") hp += s.delta;
  const dmg = hpDamageSteps(run).reduce((sum, s) => sum + s.final, 0);
  // The RECORDED damage dealt is the truth of an attack — float it directly, never the net HP swing.
  // On a structure-overflow hit the cascade resets HP up toward full, so `hp` can sum POSITIVE even
  // though the mech was hit hard; deriving the float from `hp` would render a bogus "+N heal". `dmg`
  // (post-mitigation damage actually dealt) is the incoming damage that triggered the loss, so it
  // takes priority over the HP sign.
  if (dmg > 0) return { text: `${verdict} -${dmg}`, tone: "damage" };
  if (hp < 0) return { text: `${verdict} ${hp}`, tone: "damage" }; // already carries the minus
  if (hp > 0) return { text: `+${hp}`, tone: "heal" }; // only reachable with no `damage` step — a genuine heal
  return { text: verdict, tone: "damage" }; // connected with no recorded HP swing
}

/**
 * The outcome beats of a hit: one `attack` float, or, when the run records two or more HP-dealing
 * `damage` steps (Decision 1 A), one float per step in recorded order. Only the first is an
 * `attack` beat (the shake rides it once); the rest float over the same target as `status` beats.
 */
function hitBeats(target: string, run: readonly ResolvedStep[], opts: { split: boolean; punch: number }): Beat[] {
  const sources = hpDamageSteps(run);
  const punch = opts.punch !== 1 ? { punch: opts.punch } : {};
  if (!opts.split || sources.length < 2) return [{ kind: "attack", id: target, float: attackFloat(run), ...punch }];
  return sources.map((d, n): Beat => n === 0
    ? { kind: "attack", id: target, float: attackFloat(run, d), ...punch }
    : { kind: "status", id: target, float: { text: `-${d.final} ${DTYPE_ABBREV[d.dtype]}`, tone: "damage" } });
}

/**
 * A step that changes a unit's standing or records a rolled verdict gets its own float (Decision 1
 * A): a structure loss or restore, stress loss, a table result, an interception, an NHP cascade, a
 * save's verdict over the unit that rolled it, and heat dealt to someone other than the attacker (`actor`, the latest `to_hit`'s attacker; with no
 * `to_hit` yet there is no "dealt", so self-heat from a system use stays silent). Everything else
 * returns `null` and the caller commits it silently. Floats carry numbers and verdicts, never item
 * names, except the interception, whose name is the verdict.
 */
function standingBeat(s: ResolvedStep, actor: string | undefined): Beat | null {
  switch (s.op) {
    case "structure_delta":
      if (s.delta < 0) return { kind: "status", id: s.target, float: { text: `${s.delta} STRUCTURE`, tone: "status" } };
      if (s.delta > 0) return { kind: "status", id: s.target, float: { text: `+${s.delta} STRUCTURE`, tone: "heal" } };
      return null;
    case "stress_delta":
      return s.delta < 0 ? { kind: "status", id: s.target, float: { text: `${s.delta} STRESS`, tone: "status" } } : null;
    case "heat_delta":
      return s.delta > 0 && actor !== undefined && s.target !== actor
        ? { kind: "status", id: s.target, float: { text: `+${s.delta} HEAT`, tone: "status" } }
        : null;
    case "table_check": {
      const table = s.kind === "overheating" ? "OVERHEAT" : "STRUCTURE";
      return { kind: "status", id: s.target, float: { text: `${table}: ${s.row.toUpperCase()}`, tone: "verdict" } };
    }
    case "intercept": {
      const name = s.source.name.toUpperCase();
      const outcome = s.declined === true ? "DECLINED" : s.ignored ? "SAVED" : `${s.roll ?? "?"}, FAILS`;
      return { kind: "status", id: s.target, float: { text: `${name}: ${outcome}`, tone: "verdict" } };
    }
    case "ai_cascade_check":
      return s.cascaded ? { kind: "status", id: s.target, float: { text: "NHP CASCADE", tone: "verdict" } } : null;
    case "save": {
      // A save-ends check records the literal `save_ends` rather than a stat, as the COMMS row does.
      const save = s.against === "save_ends" ? "SAVE" : `${s.against.toUpperCase()} SAVE`;
      return { kind: "status", id: s.actor, float: { text: `${save}: ${s.result.toUpperCase()}`, tone: "verdict" } };
    }
    default:
      return null;
  }
}

/**
 * Mark every float after the first over the same token as a follow-on (Decision 2 B), so the
 * renderer compresses its pacing. Keyed per token, not per resolution, so an area attack's
 * one-outcome-per-target floats keep today's full pace (Decision 3 A).
 */
function paceFollowOns(beats: readonly Beat[]): Beat[] {
  const floated = new Set<string>();
  return beats.map((b) => {
    if (!("float" in b) || b.float === null) return b;
    if (!floated.has(b.id)) {
      floated.add(b.id);
      return b;
    }
    return { ...b, float: { ...b.float, followOn: true } };
  });
}

/**
 * Compile a resolution's ordered {@link ResolvedStep} list into an ordered beat list —
 * the **event-path beat compiler**.
 * "Record fine, render coarse": a `to_hit` + its damage steps become one `AttackBeat`
 * floating the verdict and the damage (`HIT -7`; a single-target run with two or more HP-dealing
 * `damage` steps floats one per source), or a `MissBeat` on a whiff; a `status_add/remove` a
 * `StatusBeat`; a `destroy` a `DestroyBeat`. A step that changes a unit's standing or records a
 * rolled verdict floats its own beat in recorded order ({@link standingBeat}: structure, stress,
 * heat dealt, a table row, an interception, an NHP cascade, a save), whether it sits inside an attack
 * run or after it. Every other step (a resource op, …) becomes a silent `commit` beat so
 * the list **partitions** the steps — draining the queue commits every step's combatant
 * from target, so `applyResolved(prev, steps) === next` and the queue-empty invariant
 * both hold. The queue sequences the beats; a beat choreographs its own grouped run.
 *
 * **An attack** (`attack-animations` 2.1) compiles to neutral travel, then the outcome: a
 * `projectile` beat carrying the step's {@link TravelSpec} (its `fx`, recorded by the resolver), then
 * an `impact` beat carrying the outcome ({@link ImpactSpec}: the family's hit, crit or miss look at
 * the target), then the `attack`/`miss` float. Travel is the same for every outcome, so the dice gate
 * ({@link withDiceGate}) can sit between it and the impact. A `to_hit` with no `fx` plays
 * {@link fallbackFx}: a tech attack's glyph, else the default tracer.
 *
 * **`areaOrigin`**: when set, this resolution is a multi-target Blast/Burst/Line/Cone fan-out — ONE
 * shared travel at that cell (emitted once, before the first run's outcome) flies at every caught
 * target, instead of each run getting its own. A Blast then detonates there (an `impact` with
 * outcome `land`): the template always comes down, so that burst is neutral and plays before the
 * dice. Each caught target then gets its own short outcome `impact` and its float.
 *
 * **A non-weapon area action** (a Frag Grenade) records its fx on the saves it forces
 * ({@link import("@lancer-vtt/domain").SaveFx}): its throw travels once, from the thrower to the
 * action's site, before the first of those saves, and a Blast detonates there.
 *
 * **`kind`**: `ApplyResolvedCommand.kind` off the SAME broadcast resolution, threaded only as a
 * defensive scope on top of the real per-step signals — never the sole detector, since `kind` is a
 * resolution-*shape* discriminant (`"attack"` covers weapon/tech/grapple/ram/improvised alike;
 * `"action"` covers every granted-action activate, tech or not), not a per-action id.
 *   - **A no-roll quick-tech pick** (Scan/Lock On/Bolster — no `to_hit` in the resolution at all): Lock
 *     On/Bolster route through `resolveActivate`, which stamps `cause: "tech"` on any `status_add` they
 *     land — an ordinary granted action (Grapple, Brace, …)
 *     never does. Scan carries no such marker on its own `disclosure_set` step, but that op has exactly
 *     one emitter in this codebase (Scan's reveal atom), so the op itself is the tech signal. Either
 *     match ADDS a `tech_burst` beat (never replacing anything — there's no ballistic beat to replace
 *     for a non-attack resolution), alongside Lock On/Bolster's existing status float, or alone for
 *     Scan (which floats nothing today).
 *
 * Exported for the Phase-2 beat-order proof (`presentationDirector.events.test.ts`).
 */
export function compileResolution(steps: readonly ResolvedStep[], areaOrigin?: Cell, kind?: string): Beat[] {
  const beats: Beat[] = [];
  let sharedAreaBeatQueued = false;
  // Sources whose non-weapon area throw already travelled (one throw per action, however many saves).
  const thrownActions = new Set<string>();
  let actor: string | undefined;
  // Units whose save this resolution has recorded, with no structure or stress cascade on them since.
  const saved = new Set<string>();
  let i = 0;
  while (i < steps.length) {
    const s = steps[i]!;
    if (s.op === "save") saved.add(s.actor);
    if (s.op === "structure_delta" || s.op === "stress_delta" || s.op === "table_check") saved.delete(s.target);
    if (s.op === "to_hit") {
      const target = s.target;
      actor = s.attacker;
      const run: ResolvedStep[] = [s];
      let j = i + 1;
      while (j < steps.length && !breaksAttackRun(steps[j]!.op)) {
        run.push(steps[j]!);
        j++;
      }
      i = j;
      // The run's own standing steps (stress, a table result, heat dealt) float after its outcome,
      // in recorded order. They fold into the run rather than break it, so the outcome float still
      // reads the whole run's damage.
      const standing = run.flatMap((st) => standingBeat(st, actor) ?? []);
      const fx = s.fx ?? fallbackFx(s, run, kind);
      const outcome: AttackOutcome = s.result;
      const missFloat: Beat = { kind: "miss", id: target, float: { text: "MISS", tone: "miss" } };
      const punch = (outcome === "crit" ? 2 : 1) * (fx.family === "heavy" ? 1.6 : 1);
      const targetAnchor: AnimAnchor = { kind: "combatant", id: target };
      if (areaOrigin !== undefined) {
        // AoE fan-out: ONE shared travel at the template anchor, queued once ahead of the first
        // target's outcome — never a per-target travel. A Blast detonates where it lands, before the
        // dice: the template always comes down, whoever it catches.
        if (!sharedAreaBeatQueued) {
          sharedAreaBeatQueued = true;
          const at: AnimAnchor = { kind: "cell", cell: areaOrigin };
          beats.push({ kind: "projectile", id: s.attacker, from: s.attacker, to: at, travel: { fx, targets: areaTargets(steps, s.attacker) } });
          if (fx.area?.kind === "blast") beats.push({ kind: "impact", id: s.attacker, at, spec: { fx, from: s.attacker, outcome: "land" } });
        }
        beats.push({ kind: "impact", id: target, at: targetAnchor, spec: { fx, from: s.attacker, outcome, area: true } });
        beats.push(...(outcome === "miss" ? [missFloat] : hitBeats(target, run, { split: false, punch })));
        beats.push(...standing);
        continue;
      }
      // Single target: neutral travel, then (behind the dice gate) the family's outcome at the
      // target and the float. A miss flies too, and its whiff plays where the shot went.
      beats.push({ kind: "projectile", id: target, from: s.attacker, to: targetAnchor, travel: { fx, targets: [target] } });
      beats.push({ kind: "impact", id: target, at: targetAnchor, spec: { fx, from: s.attacker, outcome } });
      beats.push(...(outcome === "miss" ? [missFloat] : hitBeats(target, run, { split: true, punch })));
      beats.push(...standing);
      continue;
    }
    if (s.op === "save" && s.fx !== undefined && !thrownActions.has(s.fx.source)) {
      // A non-weapon area action (a Frag Grenade): its throw travels once, from the thrower to its
      // site, before the first save it forced; a Blast detonates there. The saves float as before.
      thrownActions.add(s.fx.source);
      const { source, site, ...fx } = s.fx;
      const at: AnimAnchor = site !== undefined ? { kind: "cell", cell: site } : { kind: "combatant", id: source };
      const savers = steps.flatMap((st) => (st.op === "save" && st.fx?.source === source ? [st.actor] : []));
      beats.push({ kind: "projectile", id: source, from: source, to: at, travel: { fx, targets: savers } });
      if (fx.area?.kind === "blast") beats.push({ kind: "impact", id: source, at, spec: { fx, from: source, outcome: "land" } });
    }
    if (s.op === "status_add" || s.op === "status_remove") {
      // No-roll quick-tech burst: a `status_add` the resolver stamped `cause: "tech"`
      // (Lock On / Bolster, via `resolveActivate`'s `TECH_ACTIVATE_IDS` — never a plain Grapple/Brace/
      // other granted action, which leaves `cause` undefined) gets the SAME `tech_burst` flourish an
      // Invade hit gets, queued right before its status float — every no-roll quick-tech pick gets
      // the tech treatment, not just Invade.
      if (s.op === "status_add" && s.cause === "tech" && (kind === undefined || kind === "action")) {
        beats.push({ kind: "tech_burst", id: s.target, at: { kind: "combatant", id: s.target } });
      }
      const text = `${s.op === "status_add" ? "+" : "-"}${s.status}`;
      beats.push({ kind: "status", id: s.target, float: { text, tone: "status" } });
      i++;
      continue;
    }
    if (s.op === "disclosure_set") {
      // Scan (`act_scan`) is the no-roll quick-tech pick with no status float at all today (its
      // `disclosure_set` step falls to a silent `commit`) and no `cause` marker to key on — but
      // `disclosure_set` has exactly one emitter in this codebase (Scan's own reveal atom, per its
      // step-type doc), so the op itself is the tech signal. The burst plays alone, with no float to
      // pair with.
      if (kind === undefined || kind === "action") {
        beats.push({ kind: "tech_burst", id: s.target, at: { kind: "combatant", id: s.target } });
      }
      beats.push({ kind: "commit", id: s.target, float: null });
      i++;
      continue;
    }
    // A structure loss or restore, a table result, an interception, stress, an NHP cascade, a save,
    // heat dealt: each floats its own beat (see `standingBeat`). A Custom Paint Job save is the case that
    // needs all of them: without the `intercept` and the `+1 STRUCTURE` floats the map would say the
    // mech lost a structure point it kept.
    const standing = standingBeat(s, actor);
    if (standing !== null) {
      beats.push(standing);
      i++;
      continue;
    }
    if (s.op === "destroy") {
      if (s.destroyed) beats.push({ kind: "destroyed", id: s.target, float: { text: "DESTROYED", tone: "destroy" } });
      else beats.push({ kind: "commit", id: s.target, float: null }); // an un-destroy (undo) commits silently
      i++;
      continue;
    }
    // A recorded displacement (`forced_move` — Knockback's push; or a resolution's voluntary `move`
    // step) animates a real slide instead of a silent jump. A placeable
    // from→to (both cells present and distinct) becomes a `forced_move` beat carrying the step's OWN
    // baked cells; a no-op displacement (unplaced `to`, or from === to) stays a silent commit like any
    // other leftover step. This mirrors the diff path's `move` beat, which already animates a
    // grapple-drag's mirrored push (`grapple-drag.e2e.ts`) — the event path just wasn't wired for it.
    if ((s.op === "forced_move" || s.op === "move") && s.from !== null && s.to !== null && !samePos(s.from, s.to)) {
      beats.push({ kind: "forced_move", id: s.target, from: s.from, to: s.to, ...(s.op === "forced_move" && s.teleport ? { teleport: true as const } : {}) });
      i++;
      continue;
    }
    // HP lost outside a `to_hit` run: a mine burst (`kind: "mine_detonation"`, a hand-built
    // save/mitigate/cascade loop, auto-hit victims included) or any unit's loss after its own save (a
    // HEX Charges throw, a burn tick, a dangerous-terrain check, a failed disarm). Without this the
    // loss would fall to the silent catch-all below. Float the recorded `hp_delta` over the victim
    // (shake + number, like a hit), after its save verdict: a shared `damage` step can be the
    // pre-save roll, not any victim's loss. A structure or stress cascade since the save clears it, so
    // the HP left over after a reset-to-full never floats as fresh damage.
    if (s.op === "hp_delta" && s.delta < 0 && (kind === "mine_detonation" || saved.has(s.target))) {
      beats.push({ kind: "attack", id: s.target, float: { text: String(s.delta), tone: "damage" } });
      i++;
      continue;
    }
    // A leftover step (a resource/burn/ledger op, the attacker's own heat, a no-op move, or
    // an hp_delta not under a to_hit): commit it silently so its state lands and the invariant holds.
    beats.push({ kind: "commit", id: subjectOf(s), float: null });
    i++;
  }
  return paceFollowOns(beats);
}

/**
 * The fx a `to_hit` with no recorded descriptor plays (a hand-built fixture, a record from before
 * `attack-animations`): a tech attack's glyph, else the default tracer. A tech attack is read the
 * way it always was: `vs: "edef"` with no weapon damage in its run (a Smart weapon also checks
 * E-DEFENSE, but rolls damage), under an attack-shaped `kind`.
 */
function fallbackFx(s: Extract<ResolvedStep, { op: "to_hit" }>, run: readonly ResolvedStep[], kind: string | undefined): AttackFx {
  const tech = s.vs === "edef" && run.every((st) => st.op !== "damage") && (kind === undefined || kind === "attack");
  return tech ? { family: "glyph", palette: "kinetic" } : DEFAULT_ATTACK_FX;
}

/** Every unit an area attack's `to_hit` runs name, in recorded order: who the travel flies at. */
function areaTargets(steps: readonly ResolvedStep[], attacker: string): string[] {
  return steps.flatMap((st) => (st.op === "to_hit" && st.attacker === attacker ? [st.target] : []));
}

/** Coarse equality of two map positions (handles the unplaced `null`/absent case). */
function samePos(a: Cell | null | undefined, b: Cell | null | undefined): boolean {
  const an = a ?? null;
  const bn = b ?? null;
  if (an === null || bn === null) return an === bn;
  return JSON.stringify(an) === JSON.stringify(bn);
}

/**
 * **Backpressure cap**: if the queued beats would take longer than this to drain, the Director
 * `snap`s to current instead of playing minutes of backlog (a slow client accumulating a long run of
 * LIVE resolutions while connected). Deliberately generous (10 minutes) — effectively disabled
 * pending real play data, since the shared AoE beat (one projectile+impact for the whole fan-out,
 * not one per target) already keeps a normal resolution's nominal cost low. Still a real backstop,
 * not `Infinity` — a pathological live backlog eventually snaps rather than animating forever.
 *
 * **Not a reconnect guard**: a disconnect does NOT replay a queue at all — `usePresentationDirector.ts`'s ingest effect detects
 * the disconnected→connected edge and calls `director.snap(authoritative)` unconditionally,
 * bypassing the beat queue (and this cap) entirely. This constant only ever throttles a backlog
 * accumulated while genuinely connected.
 */
export const LAG_CAP_MS = 600_000;

/**
 * Nominal backpressure estimate for a `tech_burst` beat ({@link overLagCap}): a no-roll quick-tech
 * flourish. A `projectile` counts its family's own travel instead (`attackFxTiming.ts`'s
 * `travelMs`, the table the animator paces with).
 */
const PROJECTILE_NOMINAL_MS = 60;

/**
 * Nominal backpressure estimate for a `dice` beat — how long the queue can expect to sit waiting for
 * a throw to land. **Measured, not guessed**: 700 headless throws across seven real pool shapes
 * settle at p50 0.75s / p90 1.11s, against a
 * structural maximum of 1.67s (`recordThrowBatch` force-settles at `SIM_BUDGET_FRAMES`, and
 * `DiceStage` plays back at `RECORD_FPS * PLAYBACK_RATE`). p90 is the honest estimate for a cap
 * that is about typical backlog rather than worst case, and {@link LAG_CAP_MS} is ten minutes, so
 * nothing here is close to tripping it — the point of counting it at all is that the estimate
 * doesn't silently under-report a queue that really does take this long to drain.
 *
 * Not an injected {@link BeatDurations} field, for the same reason `PROJECTILE_NOMINAL_MS` isn't:
 * it is not a renderer timing. The dice surface is its own subsystem with its own clock, and the
 * Pixi animator has no opinion about it.
 */
const DICE_NOMINAL_MS = 1100;

/**
 * Beat kinds that disclose nothing about the outcome: the shot's travel (identical for a hit, a miss
 * and a crit), and the chain's own staging (caption, ring, hold, stage). An `impact` is NOT one: it
 * draws the family's hit, crit or miss look, so it would tell the table the outcome while the dice
 * still roll. The one neutral impact is an area attack's `land` (a Blast's detonation at its
 * template cell), which happens whoever it catches.
 */
const NEUTRAL_KINDS: readonly Beat["kind"][] = ["projectile", "caption", "focus", "hold", "stage"];

function isNeutral(beat: Beat): boolean {
  return NEUTRAL_KINDS.includes(beat.kind) || (beat.kind === "impact" && beat.spec.outcome === "land");
}

/**
 * Splice a `dice` wait in front of the first beat that would **disclose** this resolution's
 * outcome. Pure, and separate from {@link compileResolution}
 * so the compilation stays a plain steps→beats function with no notion of dice.
 *
 * In front of the first *disclosing* beat, not at the head of the queue. A single-target attack
 * plays neutral travel → dice → outcome: `[projectile, dice, impact, attack]` on a hit and
 * `[projectile, dice, impact, miss]` on a miss, so the shot flies while the dice tumble and neither
 * the family's outcome look nor the number lands before they settle (`chained-resolution-sequence`;
 * the burst used to play ahead of the gate, disclosing the hit ~220 ms after the shot). A swarm's
 * drones and a strike's lunge hold over the target through the wait. An area attack keeps its
 * shared travel and a Blast's detonation ahead of the gate: the template always comes down.
 *
 * `wait === undefined` (a diff-path change, a resolution nobody is gating) returns the beats
 * unchanged, as does a resolution with nothing that discloses. The gate beat borrows the id of the
 * beat it precedes so the pump's `pending`/settle bookkeeping still sees an unbroken chain for that
 * combatant.
 */
export function withDiceGate(beats: readonly Beat[], wait?: () => Promise<void>): Beat[] {
  if (wait === undefined) return [...beats];
  const at = beats.findIndex((b) => !isNeutral(b));
  if (at === -1) return [...beats];
  const gated = [...beats];
  gated.splice(at, 0, { kind: "dice", id: beats[at]!.id, wait });
  return gated;
}

/** Beat kinds that commit their combatant's state when they play (every other kind is a flourish or a wait). */
const COMMITTING_KINDS: readonly Beat["kind"][] = ["move", "forced_move", "spawn", "destroy", "attack", "miss", "status", "destroyed", "commit"];

/** The id a `focus` beat queues under: no combatant carries it, so the ring never lags a token. */
export const FOCUS_BEAT_ID = "";

/**
 * Nominal backpressure estimate for a `caption` beat: the animator holds a caption on screen for
 * `CAPTION_PACING_MS` (`pixiAnimator.ts`), mirrored here for the same reason
 * {@link DICE_NOMINAL_MS} is a constant rather than an injected duration.
 */
const CAPTION_NOMINAL_MS = 900;

/**
 * What a held roll shows every screen while it waits (`chained-resolution-sequence` 3.2): the
 * stakes line, who may press Roll it, and the outcome the GM may peek at.
 */
export type HoldView = {
  /** The release key every client computes for this hold (`chainSegments`' `holdKey`). */
  key: string;
  /** `STRUCTURE CHECK · ELITE CATAPHRACT · 1 STRUCTURE LEFT · ROLLS 1D6`. */
  stakes: string;
  /** The unit making the roll; its controller (and the GM) may release. */
  roller: string;
  /** The recorded outcome (`Direct Hit`), for the GM's optional peek; absent for a "hold next". */
  outcome?: string;
};

/** What {@link compileChain} needs besides the segments. */
export type ChainCompileContext = {
  /** A combatant's display name, for the caption (`BERSERKER`). */
  names: (id: string) => string;
  /** The primary action's area template anchor ({@link compileResolution}'s `areaOrigin`). */
  areaOrigin?: Cell;
  /** The resolution's kind ({@link compileResolution}'s `kind`); read for the primary action only. */
  kind?: string;
  /** The dice wait for one segment, or `undefined` for none ({@link withDiceGate}'s `wait`). */
  diceWait?: (segment: ChainSegment) => (() => Promise<void>) | undefined;
  /** The dice wait for a reaction's own roll (Rebound's d6), or `undefined` for none. */
  reactionDiceWait?: (reaction: ChainReaction) => (() => Promise<void>) | undefined;
  /** A held segment's release key and wait (`undefined` plays the hold straight through). */
  holdWait?: (segment: ChainSegment) => { key: string; wait: () => Promise<void> } | undefined;
  /**
   * The state each segment's beats commit from, by segment index ({@link stageStates}); the last
   * is `null` (the real authoritative state). Omitted, every beat commits from authoritative.
   */
  stages?: readonly (EncounterState | null)[];
};

/**
 * The state each segment of a chain should leave the board in: `prev` with the record applied
 * through the end of that segment (its `link` marker included, a no-op). The last entry is `null`
 * so the final segment commits from the real authoritative state, which also carries what the
 * record does not (the economy fold) and keeps the queue-empty invariant exact.
 */
export function stageStates(prev: EncounterState, segments: readonly ChainSegment[]): (EncounterState | null)[] {
  let state = prev;
  return segments.map((segment, i) => {
    if (i === segments.length - 1) return null;
    state = applyResolved(state, [...(segment.link !== undefined ? [segment.link] : []), ...segment.steps, ...segment.reactions.flatMap((r) => r.steps)]);
    return state;
  });
}

/** The stakes line a held roll's banner reads. */
function holdStakes(segment: ChainSegment, names: (id: string) => string): Omit<HoldView, "key"> | undefined {
  const hold = segment.hold;
  if (hold === undefined) return undefined;
  if (hold === "start") {
    const head = segment.steps.find((s) => s.op === "to_hit" || s.op === "save");
    const roller = head?.op === "to_hit" ? head.attacker : head?.op === "save" ? head.actor : (segment.link?.actor ?? "");
    return { stakes: `THE GM HOLDS THE ROLL · ${names(roller)}`.toUpperCase(), roller };
  }
  const table = hold.kind === "overheating" ? "OVERHEAT" : "STRUCTURE";
  const track = hold.kind === "overheating" ? "STRESS" : "STRUCTURE";
  const left = hold.track !== undefined ? ` · ${hold.track} ${track} LEFT` : "";
  return { stakes: `${table} CHECK · ${names(hold.target)}${left} · ROLLS ${hold.dice.length}D6`.toUpperCase(), roller: hold.target, outcome: hold.row };
}

/**
 * Compile a resolution, cut into {@link ChainSegment}s, into beats (`chained-resolution-sequence`
 * 2.1). One segment (no `link` step) compiles exactly as {@link compileResolution} plus its dice
 * gate always has. A chain plays segment by segment: the focus ring moves to the segment's actor
 * and target, a later segment's caption (`AGGRESSION — BERSERKER`) floats over its actor, then the
 * segment's own beats play behind their own dice gate. The ring clears when the chain ends.
 *
 * Within a segment the pacing is today's `compileResolution` output for those steps, unchanged.
 */
export function compileChain(segments: readonly ChainSegment[], ctx: ChainCompileContext): Beat[] {
  if (segments.length === 1 && segments[0]!.hold === undefined && segments[0]!.reactions.length === 0) {
    const only = segments[0]!;
    return withDiceGate(compileResolution(only.steps, ctx.areaOrigin, ctx.kind), ctx.diceWait?.(only));
  }
  const beats: Beat[] = [];
  for (const segment of segments) {
    const stage = ctx.stages?.[segment.index];
    if (stage !== undefined) beats.push({ kind: "stage", id: FOCUS_BEAT_ID, state: stage });
    const focus = segmentFocus(segment);
    if (focus.length > 0) beats.push({ kind: "focus", id: FOCUS_BEAT_ID, ids: focus });
    const link = segment.link;
    if (link !== undefined) beats.push({ kind: "caption", id: link.actor, text: `${linkCauseName(link)} — ${ctx.names(link.actor)}`.toUpperCase() });
    const stakes = holdStakes(segment, ctx.names);
    const held = stakes !== undefined ? ctx.holdWait?.(segment) : undefined;
    if (stakes !== undefined && held !== undefined) beats.push({ kind: "hold", id: stakes.roller, hold: { key: held.key, ...stakes }, wait: held.wait });
    const compiled = segment.index === 0 ? compileResolution(segment.steps, ctx.areaOrigin, ctx.kind) : compileResolution(segment.steps);
    beats.push(...withReactions(withDiceGate(compiled, ctx.diceWait?.(segment)), { segment, focus, ctx }));
  }
  beats.push({ kind: "focus", id: FOCUS_BEAT_ID, ids: [] });
  return beats;
}

/**
 * Splice a segment's reactions (`chained-resolution-sequence` 4.3) into its compiled beats, right
 * after its dice gate and before its outcome: the shot flies, the dice land, the target braces (the
 * ring moves to it, its caption floats, its own floats play), and then the damage lands. A
 * reaction's floats are `flash` beats, which commit nothing: the owner's own beats, and a trailing
 * `commit` per reactor, land the state, so the board never shows the damage before its number.
 */
function withReactions(beats: Beat[], at: { segment: ChainSegment; focus: string[]; ctx: ChainCompileContext }): Beat[] {
  const { segment, focus, ctx } = at;
  if (segment.reactions.length === 0) return beats;
  const spliced: Beat[] = [];
  const touched = new Set<string>();
  for (const reaction of segment.reactions) {
    const { link } = reaction;
    spliced.push({ kind: "focus", id: FOCUS_BEAT_ID, ids: [link.actor] });
    spliced.push({ kind: "caption", id: link.actor, text: `${linkCauseName(link)} — ${ctx.names(link.actor)}`.toUpperCase() });
    for (const beat of withDiceGate(compileResolution(reaction.steps), ctx.reactionDiceWait?.(reaction))) {
      touched.add(beat.id);
      if (beat.kind === "commit") continue;
      spliced.push(beat.kind === "attack" || beat.kind === "miss" || beat.kind === "status" || beat.kind === "destroyed" ? { kind: "flash", id: beat.id, float: beat.float } : beat);
    }
    touched.add(link.actor);
  }
  if (focus.length > 0) spliced.push({ kind: "focus", id: FOCUS_BEAT_ID, ids: focus });
  const gate = beats.findIndex((b) => b.kind === "dice");
  const at0 = gate !== -1 ? gate + 1 : Math.max(0, beats.findIndex((b) => !isNeutral(b)));
  const out = [...beats.slice(0, at0), ...spliced, ...beats.slice(at0)];
  for (const id of touched) out.push({ kind: "commit", id, float: null });
  return out;
}

/** The tokens a segment's focus ring circles: its link's actor and target, else its first attack's attacker and target. */
function segmentFocus(segment: ChainSegment): string[] {
  if (segment.hold !== undefined && segment.hold !== "start") return [segment.hold.target];
  const link = segment.link;
  if (link !== undefined) return link.target !== undefined && link.target !== link.actor ? [link.actor, link.target] : [link.actor];
  const head = segment.steps.find((s) => s.op === "to_hit");
  if (head?.op !== "to_hit") return [];
  return head.attacker !== head.target ? [head.attacker, head.target] : [head.attacker];
}

/**
 * Nominal wall-clock duration of each beat *kind*, used only to estimate a queue's drain time for the
 * backpressure cap — never to drive an actual tween (the {@link PresentationAnimator}
 * owns the real timings). Injected so the Director core stays renderer-agnostic: the React adapter
 * passes the live Pixi constants (`MOVE_STEP_MS` / `FLOURISH_MS` / `FLOAT_PACING_MS` /
 * `FOLLOW_ON_FLOAT_PACING_MS`), tests pass their own. `moveStepMs` is *per tile* (a walk's estimate
 * is steps × this); `followOnFloatMs` paces a {@link FloatSpec.followOn} float.
 */
export type BeatDurations = { moveStepMs: number; flourishMs: number; floatMs: number; followOnFloatMs: number };

/** Fallback nominal durations (mirror the Pixi animator's constants; the hook injects the live ones). */
const DEFAULT_BEAT_DURATIONS: BeatDurations = { moveStepMs: 130, flourishMs: 160, floatMs: 750, followOnFloatMs: 400 };

/**
 * Dev/test-only warn, for the invariant-safety net. `import.meta.env.PROD` is statically `true` in a
 * Vite production build — so this whole call is dead-code-eliminated from a shipped bundle — `false`
 * in dev, and `undefined` under the bun test runner; the warn therefore fires in development AND in
 * tests (so the safety net is assertable), never in production.
 */
function devWarn(message: string): void {
  if (import.meta.env?.PROD !== true) console.warn(message);
}

export class PresentationDirector {
  /** The shadow state the map renders — the Director is its sole writer. `null` until the first seed. */
  private presentation: EncounterState | null = null;
  /** The latest authoritative state the queue is draining toward (the invariant's right-hand side). */
  private target: EncounterState | null = null;
  /** The strictly serial beat queue (Lancer is turn-serial — one queue, no causal-group parallelism). */
  private readonly queue: Beat[] = [];
  /** Ids with a queued-or-running beat, by kind — dedups re-ingests and drives the lag in {@link reconcileImmediate}. */
  private readonly pending = new Map<string, Beat["kind"]>();
  /**
   * Ids with an explicit outstanding hold noted via {@link noteMoveHeld} — a move-checkpoint offer in
   * flight server-side for that id. Distinct from {@link
   * pending} (which tracks a QUEUED beat): a held move produces NO positional diff at all (the mover
   * sits at `from` the whole time it's held), so `ingest`'s diff sees it as identical to an outright
   * reject/no-op. `heldIds` is the one fact {@link watchSettle} cannot derive from the authoritative
   * delta alone — the caller supplies it.
   */
  private readonly heldIds = new Set<string>();
  /**
   * Per-id one-shot settle listeners registered via {@link watchSettle} — the drag-ghost teardown seam.
   */
  private readonly settleWatchers = new Map<string, Set<() => void>>();
  /** True while the pump loop is draining the queue. */
  private running = false;
  /** The hold now waiting on a release (Roll it), or `null`; read by the stakes banner via {@link getHold}. */
  private holding: HoldView | null = null;
  /**
   * The state the current chain segment's beats commit from (`stage` beats set it), or `null` for
   * the authoritative {@link target}. See {@link stageStates}.
   */
  private stageTarget: EncounterState | null = null;
  /** Combatants committed from a {@link stageTarget}: re-committed from authoritative when the chain's last segment begins. */
  private readonly stagedIds = new Set<string>();
  /**
   * Generation token: {@link snap} bumps it to abort an in-flight pump (the loop bails when its
   * captured generation goes stale), so a hard catch-up never races a half-played beat to commit.
   */
  private gen = 0;
  private readonly listeners = new Set<() => void>();
  /** The published snapshot reference (swapped on every change so `useSyncExternalStore` re-reads). */
  private snapshotRef: EncounterState | null = null;

  /**
   * @param animator  the renderer's animation surface (the one renderer-coupled seam).
   * @param planMove  pure route→world-px geometry (null for a teleport).
   * @param durations nominal per-kind beat durations for the backpressure estimate —
   *                  injected so the core carries no renderer timings; defaults mirror the animator.
   */
  constructor(
    private readonly animator: PresentationAnimator,
    private readonly planMove: MovePlanner,
    private readonly durations: BeatDurations = DEFAULT_BEAT_DURATIONS,
  ) {}

  // ── Read surface (the React adapter subscribes here) ──────────────────────────

  /** The presentation state the map renders, or `null` before the first seed. */
  getSnapshot(): EncounterState | null {
    return this.snapshotRef;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Is a beat playing or queued? Drives the map's transient "skip animations" affordance.
   * The Director publishes on every queue change *and* once more when the queue settles (the pump's
   * exit), so a subscriber reading this through {@link subscribe} sees it flip true→false the moment
   * the last beat ends.
   */
  isAnimating(): boolean {
    return this.running || this.queue.length > 0;
  }

  /** The held roll now waiting on Roll it, or `null` (`chained-resolution-sequence` 3.2). */
  getHold(): HoldView | null {
    return this.holding;
  }

  /**
   * Whether a held roll is playing or still queued (3.3): while one is, read-only vitals displays
   * read the presentation state rather than authoritative, so they don't announce what the table
   * has not rolled yet.
   */
  hasHoldAhead(): boolean {
    return this.holding !== null || this.queue.some((b) => b.kind === "hold");
  }

  private publish(): void {
    this.snapshotRef = this.presentation;
    for (const l of this.listeners) l();
  }

  // ── Catch-up primitive (one mechanism for reconnect / skip / backpressure) ────

  /**
   * The one catch-up primitive: `presentation := target`, clear the queue, and abort any in-flight
   * beat. Hard-snap — no fast-forward. Wired to the welcome seed / reconnect, and to backpressure +
   * skip.
   */
  snap(authoritative: EncounterState): void {
    this.target = authoritative;
    this.presentation = authoritative;
    this.queue.length = 0;
    this.pending.clear();
    this.heldIds.clear();
    this.holding = null;
    this.stageTarget = null;
    this.stagedIds.clear();
    this.gen += 1; // abort a running pump before it can commit a stale beat
    this.running = false; // the aborted pump may never wake (a held roll); the next ingest pumps afresh
    this.animator.cancelAll();
    this.publish();
    // A hard catch-up resolves EVERYONE — fire every outstanding settle watcher: nothing is left
    // mid-flight once presentation === target.
    for (const id of [...this.settleWatchers.keys()]) this.fireSettle(id);
  }

  /**
   * Register interest in combatant `id`'s next terminal move resolution — the drag-ghost's teardown
   * seam. Fires `onSettle` exactly once, covering every terminal shape the ghost must never outlive:
   *  - **arrival** — a queued `move`/`forced_move` beat for `id` fully drains, whatever cell
   *    it actually lands on (the dragged cell, or a server-adjusted one — the pump's own bookkeeping
   *    below, never a hand-rolled position check);
   *  - **reject / no-walk** — a later {@link ingest}/{@link ingestResolution} processes `id` with
   *    no beat queued and no outstanding hold ({@link noteMoveHeld}): fires immediately, right there —
   *    there is no beat to await;
   *  - **removal mid-pending** — `id` drops out of `target` entirely (e.g. a reaction that
   *    destroys the mover); its `destroy` beat drains through the SAME pump cleanup as arrival.
   *
   * Does **NOT** fire while {@link noteMoveHeld} marks `id` held — a pending checkpoint offer
   * is not terminal. Returns an unsubscribe; safe to call after the listener has already fired (a
   * harmless no-op) or to never call at all (an unfired watcher is inert, just unswept memory).
   */
  watchSettle(id: string, onSettle: () => void): () => void {
    let set = this.settleWatchers.get(id);
    if (set === undefined) {
      set = new Set();
      this.settleWatchers.set(id, set);
    }
    set.add(onSettle);
    return () => {
      const s = this.settleWatchers.get(id);
      if (s === undefined) return;
      s.delete(onSettle);
      if (s.size === 0) this.settleWatchers.delete(id);
    };
  }

  /**
   * Caller-supplied signal: true while a move-checkpoint offer holds `id`'s move at the
   * server — the one fact {@link watchSettle} cannot derive from the authoritative diff alone (a held
   * move and an outright reject both produce zero positional delta, so `ingest` sees them identically).
   * Set true the instant the caller learns of a hold; false the instant it resolves. Clearing it
   * re-checks whether `id` is ALREADY settled (a resolved hold that produced no beat — e.g. a decline
   * that leaves the mover exactly where it was) and fires right away rather than waiting on a beat that
   * may never come.
   */
  noteMoveHeld(id: string, held: boolean): void {
    if (held) {
      this.heldIds.add(id);
      return;
    }
    this.heldIds.delete(id);
    if (!this.pending.has(id)) this.fireSettle(id);
  }

  /** Fire and clear every listener registered for `id` via {@link watchSettle}; a no-op if none are. */
  private fireSettle(id: string): void {
    const set = this.settleWatchers.get(id);
    if (set === undefined) return;
    this.settleWatchers.delete(id);
    for (const cb of set) cb();
  }

  /**
   * User **skip**: abandon the remaining animation and jump the map to current. This is
   * NOT a second catch-up mechanism — it is the one {@link snap} primitive triggered by a user
   * gesture. Hard-snap; there is no high-speed fast-forward. No-op before the first seed (nothing to
   * catch up to).
   */
  skip(): void {
    if (this.target === null) return;
    // A skip stops at a held roll (`decisions/0210` item 4): it fast-forwards to the hold, never
    // past it, so nobody skips the table's dramatic pause. Sitting at a hold, it does nothing.
    if (this.holding !== null) return;
    const at = this.queue.findIndex((b) => b.kind === "hold");
    if (at === -1) return this.snap(this.target);
    this.fastForwardTo(at);
  }

  /**
   * Drop the beats ahead of queue index `at` without animating them, landing their state as they
   * would have (each committing beat's combatant from its segment's stage, the ring and the stage
   * moved on), then resume the pump at the hold.
   */
  private fastForwardTo(at: number): void {
    const dropped = this.queue.splice(0, at);
    this.gen += 1; // abort the beat now playing; its own commit already landed or is guarded out
    this.animator.cancelAll();
    for (const beat of dropped) {
      if (beat.kind === "stage") this.setStage(beat.state);
      else if (beat.kind === "focus") this.animator.focus(beat.ids);
      else if (beat.kind === "attack" || beat.kind === "miss" || beat.kind === "status" || beat.kind === "destroyed" || beat.kind === "commit") this.commitFromTarget(beat.id);
      else if (beat.kind === "move" || beat.kind === "forced_move" || beat.kind === "spawn" || beat.kind === "destroy") this.commitFromTarget(beat.id, true);
      if (!this.queue.some((b) => b.id === beat.id)) this.pending.delete(beat.id);
    }
    this.running = false;
    this.publish();
    void this.pump();
  }

  /**
   * Commit an own-action's destination to presentation **immediately** (optimistic dedup).
   * The view calls this the instant a local `move_token`
   * is accepted: the drag *was* the animation, so presentation jumps to the dropped cell and
   * the server echo then diffs to a no-op (no re-walk). A server-*adjusted* echo diffs to a
   * short corrective slide; a reject reconciles back when the controller snaps authoritative.
   */
  commitLocalMove(id: string, pos: Cell): void {
    if (this.presentation === null) return;
    const c = this.presentation.combatants[id];
    if (c === undefined) return;
    this.presentation = {
      ...this.presentation,
      combatants: { ...this.presentation.combatants, [id]: { ...c, pos } },
    };
    this.publish();
  }

  // ── Diff path: synthesize beats from the authoritative delta ─────────────────

  /**
   * Ingest a fresh authoritative state (a plain `encounter_change` with no resolution rider —
   * the *diff path*). Synthesizes beats from
   * `diff(presentation, authoritative)` (NOT old-authoritative — that is what dedupes own-moves
   * and yields corrective slides for free), commits all *non-animated* state immediately so the
   * map stays fresh, then pumps. The very first ingest (no presentation yet) seeds via {@link snap}.
   */
  ingest(authoritative: EncounterState): void {
    if (this.presentation === null) {
      this.snap(authoritative);
      return;
    }
    this.target = authoritative;

    // Spatial / existence deltas → beats (skip ids already covered by a pending beat:
    // a queued move stores no destination, so it'll resolve the *latest* target at play).
    for (const [id, ac] of Object.entries(authoritative.combatants)) {
      if (this.pending.has(id)) continue;
      const pc = this.presentation.combatants[id];
      if (pc === undefined) this.enqueue({ kind: "spawn", id });
      else if (!samePos(pc.pos, ac.pos)) this.enqueue({ kind: "move", id });
    }
    for (const id of Object.keys(this.presentation.combatants)) {
      if (this.pending.has(id)) continue;
      if (authoritative.combatants[id] === undefined) this.enqueue({ kind: "destroy", id });
    }

    if (this.overLagCap()) return this.snap(authoritative); // backpressure: too much backlog → catch up
    this.sweepImmediateSettles(); // a watched id with no beat queued this round settles now

    // Converge every non-animated fact now (board, terrain, turn order, hp/status of
    // non-moving combatants) so the map only lags the things a beat is actively playing.
    this.presentation = this.reconcileImmediate(authoritative);
    this.publish();
    void this.pump();
  }

  // ── Event path: compile a resolution's steps into ordered beats ──────────────

  /**
   * Ingest an authoritative state that arrived **with** a resolution rider (the *event
   * path* — provenance is the cut). Compiles the
   * ordered `ResolvedStep[]` into beats ({@link compileResolution}) and enqueues them
   * into the SAME serial queue the diff path feeds — so an AoE's per-target outcomes
   * float **one at a time in recorded order**. The diff for this message is IGNORED (the
   * caller routes a resolution-bearing message here *instead of* {@link ingest}):
   * `applyResolved(prev, steps) === next` makes the diff redundant, and running both
   * would double-animate.
   *
   * Convergence is identical to the diff path: every non-animated fact (and every
   * combatant no beat owns) reconciles immediately; the beats' targets lag until their
   * beat commits them from target — so when the queue drains, presentation === target.
   * The very first ingest (no presentation yet) seeds via {@link snap}.
   *
   * `rider` bundles `areaOrigin` + `kind` into one options object
   * to stay within `CODE_STYLE.md`'s `max-params` cap of 3 — see {@link compileResolution}'s doc for
   * what each does.
   */
  ingestResolution(
    steps: readonly ResolvedStep[],
    authoritative: EncounterState,
    rider?: {
      areaOrigin?: Cell;
      kind?: string;
      diceWait?: (segment: ChainSegment) => (() => Promise<void>) | undefined;
      holdWait?: (segment: ChainSegment) => { key: string; wait: () => Promise<void> } | undefined;
      reactionDiceWait?: (reaction: ChainReaction) => (() => Promise<void>) | undefined;
      /** Where this resolution holds (`chainSegments`' policy); omitted, it plays straight through. */
      holds?: HoldPolicy;
    },
  ): void {
    if (this.presentation === null) {
      this.snap(authoritative);
      return;
    }
    const prevTarget = this.target;
    this.target = authoritative;
    // Enqueue every compiled beat (no per-id dedup — a kill is an attack beat AND a
    // destroyed beat on the same target, both wanted; the serial pump plays them in order).
    // `diceWait`, when the caller supplies one, adds the reveal gate in front of the first beat
    // that would disclose the outcome — see {@link withDiceGate}.
    // A chained resolution (`link` steps) plays segment by segment, each behind its own gate.
    const names = (id: string): string => authoritative.combatants[id]?.name ?? this.presentation?.combatants[id]?.name ?? id;
    const segments = chainSegments(steps, rider?.holds);
    // A chain commits segment by segment from staged states (3.3), so a held roll cannot be
    // spoiled by a token already showing its outcome.
    const stages = segments.length > 1 && prevTarget !== null ? stageStates(prevTarget, segments) : undefined;
    const ctx: ChainCompileContext = {
      names,
      ...(rider?.areaOrigin !== undefined ? { areaOrigin: rider.areaOrigin } : {}),
      ...(rider?.kind !== undefined ? { kind: rider.kind } : {}),
      ...(rider?.diceWait !== undefined ? { diceWait: rider.diceWait } : {}),
      ...(rider?.holdWait !== undefined ? { holdWait: rider.holdWait } : {}),
      ...(rider?.reactionDiceWait !== undefined ? { reactionDiceWait: rider.reactionDiceWait } : {}),
      ...(stages !== undefined ? { stages } : {}),
    };
    for (const beat of compileChain(segments, ctx)) this.enqueue(beat);

    if (this.overLagCap()) return this.snap(authoritative); // backpressure: too much backlog → catch up
    this.sweepImmediateSettles(); // a watched id with no beat queued this round settles now

    // Converge everything a beat is NOT mid-playing now (board, turn order, any combatant
    // no beat owns), keeping the beats' targets lagged until each beat commits from target.
    this.presentation = this.reconcileImmediate(authoritative);
    this.publish();
    void this.pump();
  }

  private enqueue(beat: Beat): void {
    this.queue.push(beat);
    this.pending.set(beat.id, beat.kind);
  }

  /** {@link watchSettle}'s terminal shape (iii): after this round's enqueues, any watched id with
   *  neither a queued beat nor an outstanding {@link noteMoveHeld} hold has nothing left to animate —
   *  fire its settle now instead of waiting for a beat that was never going to be queued. */
  private sweepImmediateSettles(): void {
    for (const id of [...this.settleWatchers.keys()]) {
      if (!this.pending.has(id) && !this.heldIds.has(id)) this.fireSettle(id);
    }
  }

  // ── Backpressure: a wall-clock lag cap → snap ─────────────────────────────────

  /** True when the queued beats would take longer than {@link LAG_CAP_MS} to drain — the cue to
   *  catch up via {@link snap} rather than play out a long backlog. Checked on every ingest. */
  private overLagCap(): boolean {
    let total = 0;
    for (const beat of this.queue) {
      total += this.nominalMs(beat);
      if (total > LAG_CAP_MS) return true; // short-circuit: a big backlog needn't be summed fully
    }
    return false;
  }

  /**
   * Estimate one beat's wall-clock duration for the backpressure sum — the *nominal* time its
   * renderer will take, never an exact measurement (the animator owns real timings). A move is
   * steps × {@link BeatDurations.moveStepMs} when a legal path resolves (else a flourish-length
   * blink); spawn/destroy are a fade; the event floats pace at `floatMs` (a follow-on float at
   * `followOnFloatMs`); a silent `commit` is free.
   */
  private nominalMs(beat: Beat): number {
    switch (beat.kind) {
      case "move": {
        const from = this.presentation?.combatants[beat.id]?.pos ?? null;
        const to = this.target?.combatants[beat.id]?.pos ?? null;
        if (from !== null && to !== null && !samePos(from, to)) {
          const points = this.planMove(beat.id, from, to);
          if (points !== null && points.length >= 2) return (points.length - 1) * this.durations.moveStepMs;
        }
        return this.durations.flourishMs; // a blink / a placement
      }
      case "forced_move": {
        // A recorded slide from the step's OWN baked cells (not presentation-vs-target — an earlier
        // same-target beat may have already committed the new pos into `target`).
        const points = beat.teleport ? null : this.planMove(beat.id, beat.from, beat.to);
        if (points !== null && points.length >= 2) return (points.length - 1) * this.durations.moveStepMs;
        return this.durations.flourishMs;
      }
      case "spawn":
      case "destroy":
        return this.durations.flourishMs;
      case "projectile":
        return travelMs(beat.travel.fx, beat.travel.targets.length);
      case "impact":
        // A Blast's detonation holds the board before the dice; a target's own outcome burst rides
        // under the float that follows it, which `floatMs` already counts.
        return beat.spec.outcome === "land" ? DETONATION_MS : 0;
      case "tech_burst":
        // A no-roll quick-tech pick's flourish, standing alone with nothing else to bundle it into.
        return PROJECTILE_NOMINAL_MS;
      case "dice":
        return DICE_NOMINAL_MS;
      case "caption":
        return CAPTION_NOMINAL_MS;
      case "flash":
        return beat.float.followOn === true ? this.durations.followOnFloatMs : this.durations.floatMs;
      case "focus":
      case "stage":
      case "hold":
        // A hold waits on a person, not a clock: it must never count toward the backpressure cap,
        // or a long dramatic pause would snap the board past the roll it is holding.
        return 0;
      case "attack":
      case "miss":
      case "status":
      case "destroyed":
        return beat.float.followOn === true ? this.durations.followOnFloatMs : this.durations.floatMs;
      case "commit":
        return 0; // a silent state-only beat doesn't pace the pump
    }
  }

  /**
   * Presentation := authoritative, *except* the bits a beat is mid-playing: a pending **move**
   * keeps its current presentation pos (lags) while adopting every other field; a pending **spawn**
   * stays omitted until its beat fades it in; a pending **destroy** stays present until its beat
   * fades it out. Everything else (and every unchanged combatant) adopts authoritative wholesale.
   */
  private reconcileImmediate(authoritative: EncounterState): EncounterState {
    const presentation = this.presentation!;
    const combatants: Record<string, Combatant> = {};
    for (const [id, ac] of Object.entries(authoritative.combatants)) {
      const kind = this.pending.get(id);
      if (kind === "spawn") continue; // not yet faded in
      if (kind === "move" || kind === "forced_move") {
        const pc = presentation.combatants[id];
        combatants[id] = pc ? { ...ac, pos: pc.pos } : ac; // adopt all but the (lagging) position
      } else if (kind !== undefined && kind !== "destroy") {
        // An event beat (attack/miss/status/destroyed/commit) is mid-queue for this combatant:
        // keep its lagging presentation value (HP/status/pos) wholesale until the beat commits it
        // from target — so the `-N`/status float lands as the change applies, not before. (`destroy`
        // is the diff-path remove, kept visible in the loop below.)
        const pc = presentation.combatants[id];
        combatants[id] = pc ?? ac;
      } else {
        combatants[id] = ac;
      }
    }
    // Keep a pending-destroy token visible (absent from authoritative) until its beat removes it.
    for (const [id, pc] of Object.entries(presentation.combatants)) {
      if (authoritative.combatants[id] === undefined && this.pending.get(id) === "destroy") {
        combatants[id] = pc;
      }
    }
    return { ...authoritative, combatants };
  }

  // ── The serial pump ───────────────────────────────────────────────────────────

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    const myGen = this.gen;
    try {
      while (this.queue.length > 0) {
        if (this.gen !== myGen) return; // snapped — abandon the drain
        const beat = this.queue.shift()!;
        await this.play(beat, myGen);
        if (this.gen !== myGen) return; // snapped mid-beat — its commit was already guarded out
        // Clear this id's `pending` entry ONLY when this was its LAST queued beat. A target can have
        // a same-id beat run (`projectile` → `impact` → `attack`, all for the target): the non-
        // committing leaders (projectile/impact) must NOT clear `pending` while a later same-id beat
        // still owns the lag — clearing early opens a window where an interleaved ingest (a fresh
        // authoritative arriving mid-run) sees no pending entry and adopts the new value immediately
        // in `reconcileImmediate`, instead of lagging until the final (committing) beat plays. This
        // generalizes the old `projectile`-only special-case to "skip cleanup unless this is the last
        // queued beat for this id," so a third same-id beat (impact) needs no new special case.
        if (!this.queue.some((b) => b.id === beat.id)) {
          this.pending.delete(beat.id);
          this.heldIds.delete(beat.id); // a beat draining to completion supersedes any stale hold flag
          this.fireSettle(beat.id); // arrival, a server-adjusted cell, or removal
        }
        this.publish();
      }
      // Drained normally (not snapped): the queue-empty invariant must now hold.
      this.assertReconciledToTarget();
    } finally {
      // The generation owns the `running` flag. A snap (or a skip's fast-forward) bumps the
      // generation and clears `running` itself, so a later ingest pumps at once rather than
      // stranding behind this pump, which may sit at an `await` for a long time (a held roll waits on
      // a person) or forever. When this stale pump finally wakes it must leave `running` alone: a
      // newer pump may own it. A pump of the current generation re-pumps anything enqueued while it
      // ran, and publishes once so a subscriber sees `isAnimating` settle to false.
      if (this.gen === myGen) {
        this.running = false;
        if (this.queue.length > 0) void this.pump();
        else this.publish();
      }
    }
  }

  /**
   * Play one beat: mutate presentation + await the renderer. The `switch` is the open-ended
   * beat vocabulary — a new beat kind is a case here, never a Director-structure edit. `gen` is the
   * pump's generation; a post-`await` commit is skipped if a {@link snap} bumped it mid-beat (the
   * snap already jumped presentation to current, so the stale beat must not write over it).
   */
  private async play(beat: Beat, gen: number): Promise<void> {
    switch (beat.kind) {
      case "move":
        return this.playMove(beat.id, gen);
      case "forced_move":
        return this.playForcedMove(beat, gen);
      case "spawn":
        return this.playSpawn(beat.id, gen);
      case "destroy":
        return this.playDestroy(beat.id, gen);
      case "projectile":
        return this.playProjectile(beat);
      case "impact":
        return this.playImpact(beat);
      case "tech_burst":
        return this.playTechBurst(beat);
      case "dice":
        return this.playDice(beat);
      case "caption":
        return this.animator.float(beat.id, { text: beat.text, tone: "caption" });
      case "flash":
        return this.animator.float(beat.id, beat.float);
      case "focus":
        this.animator.focus(beat.ids);
        return;
      case "stage":
        this.setStage(beat.state);
        return;
      case "hold":
        return this.playHold(beat, gen);
      case "attack":
      case "miss":
      case "status":
      case "destroyed":
      case "commit":
        return this.playEvent(beat, gen);
    }
  }

  /**
   * Play a `projectile` beat: just the renderer travel flourish — no
   * state to commit (the following outcome beat commits the target). Awaited like every other beat
   * so the queue stays strictly serial: the projectile finishes traveling before its attack/miss
   * outcome plays.
   */
  private async playProjectile(beat: Extract<Beat, { kind: "projectile" }>): Promise<void> {
    await this.animator.projectile(beat.from, beat.to, beat.travel);
  }

  /**
   * Play an `impact` beat: just the renderer burst flourish at the
   * target's cell — no state to commit (the following `attack` beat commits the target). Queued only
   * on a hit, immediately after its `projectile` beat and before its `attack` outcome. Awaited like
   * every other beat so the queue stays strictly serial: the burst finishes before the outcome floats.
   */
  private async playImpact(beat: Extract<Beat, { kind: "impact" }>): Promise<void> {
    // Fire the impact cue as a parallel side-effect BEFORE awaiting the visual burst so it lands in
    // sync with the flourish — `sound` returns `void` (never awaited/wrapped into this beat's Promise),
    // so it can neither delay nor fail the beat's timing.
    // A miss's whiff is silent here: its swoosh rides the `miss` float beat that follows.
    if (beat.spec.outcome !== "miss") this.animator.sound("impact");
    await this.animator.impact(beat.at, beat.spec);
  }

  /**
   * Play a `tech_burst` beat: the quick-tech flourish — no state
   * to commit (the following `attack`/`status`/`commit` beat commits the target). No sound cue: unlike
   * `impact`, there's no dedicated tech SFX yet (`sound-effects` scope, not this phase) — silent is the
   * simple default. Awaited like every other beat so the queue stays strictly serial.
   */
  /**
   * Play a `dice` beat: wait, and animate nothing. No state to
   * commit and no renderer call at all — the beat exists purely to keep the serial queue from
   * reaching the outcome beat behind it until this resolution's dice have stopped rolling.
   *
   * The wait cannot hang: every path through {@link ../dice/resolutionThrows} resolves, either on
   * the real settle or on its own ceiling. A snap arriving mid-wait doesn't need special handling
   * here — the pump re-checks its generation after every beat and abandons the drain, so this beat
   * finishes its wait into a queue that has already been cleared.
   */
  private async playDice(beat: Extract<Beat, { kind: "dice" }>): Promise<void> {
    await beat.wait();
  }

  /**
   * Move to a chain segment's stage. Entering the last segment (`null`) re-commits, from
   * authoritative, every combatant an earlier segment committed from its stage and no later beat
   * owns, so the drained board holds authoritative's own objects (the queue-empty invariant).
   */
  private setStage(state: EncounterState | null): void {
    this.stageTarget = state;
    if (state !== null) return;
    for (const id of [...this.stagedIds]) {
      if (!this.queue.some((b) => b.id === id && COMMITTING_KINDS.includes(b.kind))) this.commitFromTarget(id);
    }
    this.stagedIds.clear();
  }

  /**
   * Play a `hold` beat: publish its stakes (every screen's banner reads {@link getHold}), wait for
   * its release, then clear it. No state to commit. A snap mid-wait clears `holding` itself and
   * abandons the drain, so a stale release finishing here must not clear a newer hold.
   */
  private async playHold(beat: Extract<Beat, { kind: "hold" }>, gen: number): Promise<void> {
    this.holding = beat.hold;
    this.publish();
    await beat.wait();
    if (this.gen !== gen) return;
    this.holding = null;
    this.publish();
  }

  private async playTechBurst(beat: Extract<Beat, { kind: "tech_burst" }>): Promise<void> {
    await this.animator.techBurst(beat.at);
  }

  /**
   * Play one event-path beat: **commit the grouped steps'
   * state** by adopting the target's authoritative value (reading the whole combatant
   * from target keeps queue-empty ⇒ presentation === authoritative a literal identity —
   * the same convergence the diff path uses), then **float** the outcome above the
   * target. A `commit` beat carries no float (a silent step-run that just lands state).
   * The float `await` is what serializes an AoE's outcomes one at a time.
   *
   * An `attack` beat — and ONLY `attack` (a hit that dealt damage,
   * never a `miss`/`status`/`destroyed`) — also **shakes + dark-red pulses** the target, run
   * CONCURRENTLY with the float so the punch reads as the `-N` rises. Both are awaited (via
   * `Promise.all`), so the beat's `Promise` resolves only once BOTH have settled — and
   * `shakeAndPulse` restores the node to its EXACT rest position before it resolves, so the shake
   * (the first beat to tween a node's *position* for a non-move reason) never leaves the renderer a
   * position to fight. It rides this existing committing beat: no new Beat kind, no queue entry, no
   * nominal-duration constant (the shake is shorter than the float's pacing it overlaps).
   */
  private async playEvent(beat: Extract<Beat, { kind: EventBeatKind }>, gen: number): Promise<void> {
    this.commitFromTarget(beat.id);
    if (beat.float === null || this.gen !== gen) return;
    // A whiffed attack's distinct swoosh, fired as a parallel side-effect in sync with the MISS float
    // (the hit-connect cue rides the `impact` beat in `playImpact`). `sound` is `void`, never awaited —
    // it can't delay or fail this beat.
    if (beat.kind === "miss") this.animator.sound("miss");
    const float = this.animator.float(beat.id, beat.float);
    if (beat.kind === "attack") await Promise.all([float, this.animator.shakeAndPulse(beat.id, beat.punch)]);
    else await float;
  }

  private async playMove(id: string, gen: number): Promise<void> {
    const from = this.presentation?.combatants[id]?.pos ?? null;
    const to = this.target?.combatants[id]?.pos ?? null;
    // A placement (null → cell) or a leave-board (cell → null) has no route to walk: just commit.
    // A genuine cell→cell delta walks if a legal path exists, else blinks (teleport / GM-place).
    if (from !== null && to !== null && !samePos(from, to)) {
      const points = this.planMove(id, from, to);
      if (points !== null && points.length >= 2) {
        await this.animator.move(id, points);
        if (this.gen === gen) this.commitFromTarget(id, true);
        return;
      }
      this.commitFromTarget(id, true); // teleport: jump the state, then blink in place
      await this.animator.blink(id);
      return;
    }
    this.commitFromTarget(id, true);
    if (from === null && to !== null) await this.animator.blink(id); // a fresh placement reads as an arrival
  }

  /**
   * Play a `forced_move` beat — a Knockback push / resolution move animated as a
   * real slide. Unlike {@link playMove} it walks the step's OWN baked `from`→`to` cells (a resolution's
   * displacement is resolved-once, recorded, and replayed), so the slide is correct even
   * though an earlier same-target beat (the attack outcome) already ran — {@link commitFromTarget}
   * keeps this combatant's pos lagged until here precisely because a `forced_move` beat still owns it.
   * A degenerate path (no legal route) blinks in place, matching {@link playMove}'s teleport fallback.
   */
  private async playForcedMove(beat: Extract<Beat, { kind: "forced_move" }>, gen: number): Promise<void> {
    const points = beat.teleport ? null : this.planMove(beat.id, beat.from, beat.to);
    if (points !== null && points.length >= 2) {
      await this.animator.move(beat.id, points);
      if (this.gen === gen) this.commitFromTarget(beat.id, true);
      return;
    }
    this.commitFromTarget(beat.id, true); // a teleport, or no route (off-map / no answer): jump the state, then blink
    if (this.gen === gen) await this.animator.blink(beat.id);
  }

  private async playSpawn(id: string, gen: number): Promise<void> {
    this.commitFromTarget(id, true); // the token appears at full state; the fade is the entrance
    if (this.gen === gen) await this.animator.fadeIn(id);
  }

  private async playDestroy(id: string, gen: number): Promise<void> {
    await this.animator.fadeOut(id); // fade the still-present node, then remove it from state
    if (this.gen === gen) this.commitFromTarget(id);
  }

  /**
   * Commit combatant `id` to exactly its authoritative value (or remove it if the target dropped
   * it) — the per-beat convergence step. Reading the whole combatant from `target` (not a hand-built
   * partial) is what keeps the queue-empty invariant a literal identity.
   *
   * @param ownsPos  true when THIS beat's own job is resolving `id`'s position (a `move`/`forced_move`/
   * `spawn` commit calling in after its own walk/placement — presentation's pos is EXPECTED to still
   * lag target's here, and this exact call is what's supposed to converge it, same as always). false
   * for an event-path beat (attack/miss/status/destroyed/commit) that never owned position — see the
   * `else` branch below for why a residual diff there needs different handling.
   */
  private commitFromTarget(id: string, ownsPos = false): void {
    if (this.presentation === null) return;
    const tc = (this.stageTarget ?? this.target)?.combatants[id];
    if (this.stageTarget !== null) this.stagedIds.add(id);
    const combatants = { ...this.presentation.combatants };
    if (tc === undefined) delete combatants[id];
    else if (this.queue.some((b) => b.id === id && (b.kind === "move" || b.kind === "forced_move"))) {
      // A LATER move/forced_move beat still owns this combatant's position (a Ram commits the hit's
      // HP/status here, but a `forced_move` slide is queued behind it): adopt every field EXCEPT pos,
      // keeping the lagging presentation pos so the slide runs from the OLD cell. Without this,
      // this commit would jump pos to the knocked-back cell and the later slide
      // would find `from === to`. Mirrors `reconcileImmediate`'s pos-lag for a pending move beat.
      const pc = this.presentation.combatants[id];
      combatants[id] = pc ? { ...tc, pos: pc.pos } : tc;
    } else if (!ownsPos) {
      const pc = this.presentation.combatants[id];
      if (pc !== undefined && !samePos(pc.pos, tc.pos)) {
        // A fresh authoritative position landed for `id` while THIS (non-move) beat was the one
        // committing it, with no move/forced_move beat queued yet to own it — the take-Overwatch
        // gap: the Overwatch shot's own attack-resolution beat targets the
        // mover, and the held move's SEPARATE later broadcast (applying `from`→`to`) can arrive while
        // that attack beat is still queued/playing for the same id. `ingest`'s diff path sees
        // `pending.has(id)` already true and skips re-checking position (correct for a pending
        // move/forced_move, which resolves its route at PLAY time — wrong here, since an event beat
        // reads `target` exactly once, right now). Adopting `tc` wholesale would silently teleport the
        // combatant — and on an `attack` beat it's worse than cosmetic: it races `shakeAndPulse`, which
        // captures the container's rest position synchronously (before React can reposition it for
        // this very commit) and unconditionally restores that STALE pre-jump position when its tween
        // ends, permanently stranding the token on screen even though the model has already converged.
        // Mirror the look-ahead branch above instead: keep pos lagged and queue a real `move` beat so
        // the walk plays properly once this beat's own commit is done.
        combatants[id] = { ...tc, pos: pc.pos };
        this.enqueue({ kind: "move", id });
      } else {
        combatants[id] = tc;
      }
    } else {
      combatants[id] = tc;
    }
    this.presentation = { ...this.presentation, combatants };
    this.publish();
  }

  // ── Invariant-violation safety: trust the invariant, snap on a miss ───────────

  /**
   * Post-drain safety net: when the queue empties the
   * core invariant says presentation === authoritative. If a drained queue left them diverged — a
   * beat that failed to commit its target, a combatant no beat covered (e.g. a future beat kind that
   * lags something {@link reconcileImmediate} doesn't converge, or a fresh authoritative arriving for
   * a combatant mid-beat) — we DON'T silently diff-patch back to truth; we **trust the invariant** and
   * hard-{@link snap} to authoritative (the one catch-up primitive), with a dev/test-only warn so the
   * latent bug is loud in development and silent in a shipped bundle.
   */
  private assertReconciledToTarget(): void {
    if (this.target === null || this.presentation === null) return;
    if (this.reconciledToTarget()) return;
    devWarn(
      "[PresentationDirector] queue drained but presentation diverged from authoritative — snapping to recover. This is a bug: a beat failed to reconcile its target.",
    );
    this.snap(this.target);
  }

  /**
   * Cheap convergence check for {@link assertReconciledToTarget}: every beat commits its combatant by
   * adopting the *target's* object (`commitFromTarget`), and {@link reconcileImmediate} adopts every
   * non-lagging combatant the same way — so a fully-drained presentation holds the **same object
   * reference** per combatant as target. Reference equality (plus matching id sets) is therefore the
   * invariant, with no deep walk. Non-combatant fields are copied wholesale from authoritative and
   * never lagged, so they need no check.
   */
  private reconciledToTarget(): boolean {
    const p = this.presentation!;
    const t = this.target!;
    const tIds = Object.keys(t.combatants);
    if (Object.keys(p.combatants).length !== tIds.length) return false;
    for (const id of tIds) {
      if (p.combatants[id] !== t.combatants[id]) return false;
    }
    return true;
  }
}
